import {
  faCircleArrowDown,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faCrown } from '@fortawesome/free-solid-svg-icons/faCrown';
import { ColorPicker, Tooltip } from 'antd';
import { t } from 'i18next';
import debounce from 'lodash-es/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraEmpty } from '../../../components/AtiraEmpty';
import { AtiraIcon } from '../../../components/AtiraIcon';
import { AtiraSelect } from '../../../components/AtiraSelect';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { AtiraImage } from '../../../components/Image';
import { InfoNotice } from '../../../components/InfoNotice';
import { Input } from '../../../components/Input';
import { Text } from '../../../components/Text';
import { Modal } from '../../../components/modal/Modal';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { PageMeta } from '../../../model/meta/PageMeta';
import { DataType } from '../../../model/qr/Types/DataType.enum';
import { QRFormat } from '../../../model/qr/Types/QRFormat.enum';
import { CreateQRDto } from '../../../model/qr/dto/CreateQRDto';
import { usePlansContext } from '../../../plans-context';
import { QRSliceSelectors } from '../../../redux/QR/qr.selector';
import { QRActions } from '../../../redux/QR/qr.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { QRUtils } from '../QRUtils';

const StyledInput = styled(Input)<{ direction?: string }>`
  padding: ${Spacing.s} ${Spacing.s};
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.fgMuted};
  display: inline-block;
  margin-inline-start: ${Spacing.s};
`;

const CreateButton = styled(Button)`
  width: 8rem;
  height: 2.5rem;
`;

const CreationContent = styled(Flex)`
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.xl};
`;

const MiniColorPicker = styled(ColorPicker)`
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.inputBorder};

  &:hover {
    border: 1px solid ${({ theme }) => theme.inputBorder};
  }

  .ant-color-picker-color-block {
    width: 100%;
    height: 100%;
  }

  &.ant-color-picker-trigger-active {
    box-shadow: none;
  }
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xl};
  width: 100%;

  @media (min-width: ${Breakpoints.MOBILE}) {
    flex-direction: row;
  }
`;

const ResponseWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  max-width: 100%;

  @media (min-width: ${Breakpoints.TABLET}) {
    width: 30%;
  }
`;

const StyledNotice = styled(InfoNotice)`
  @media (min-width: ${Breakpoints.MOBILE}) {
    display: none;
  }
`;

const dataTypesOptions = [
  { value: DataType.URL, label: t('common.url') },
  { value: DataType.EMAIL, label: t('common.email') },
  { value: DataType.PHONE, label: t('common.phone') },
  { value: DataType.SMS, label: t('common.sms') },
  { value: DataType.TEXT, label: t('common.text') },
];

const formatOptions = [
  { value: QRFormat.PNG, label: 'PNG' },
  { value: QRFormat.SVG, label: 'SVG' },
];

type QRGeneratorModalProps = {
  open: boolean;
  setOpen: ATVoidFunction<boolean>;
};

export const QRGeneratorModal: React.FC<QRGeneratorModalProps> = ({
  open,
  setOpen,
}) => {
  const { t } = useTranslation();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const userQRsLength = useAppSelector(QRSliceSelectors.selectAllQRs).length;

  const [loading, setLoading] = useState(false);
  const [previewURL, setPreviewURL] = useState('');

  const [reachedLimit, setReachedLimit] = useState(
    Number(loggedInUser.plan?.perks?.qrCount) <= userQRsLength,
  );

  const { control, getValues, handleSubmit, reset, watch, setError } =
    useForm<CreateQRDto>({
      defaultValues: {
        color: '#000000',
        bgcolor: '#ffffff',
        format: QRFormat.PNG,
        dataType: DataType.URL,
        size: 250,
        data: '',
      },
    });

  const { setPlansModalVisible } = usePlansContext();
  const { width } = useWindowDimensions();

  const selectedDataType = watch('dataType', DataType.URL);
  const selectedFormat = watch('format', QRFormat.PNG);

  const dispatch = useAppDispatch();

  const isOnMobile = width < Number(Breakpoints.MOBILE.replace('px', ''));

  const dto = getValues();

  const data = watch('data');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generateQRPreview = useCallback(
    debounce(() => {
      const { size, bgcolor, color, data, format, dataType } = getValues();

      const prefixedData = encodeURIComponent(
        QRUtils.transformDataBasedOnType(data, dataType),
      );

      if (!data) {
        return;
      }

      const imgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}&data=${prefixedData}&bgcolor=${encodeURIComponent(bgcolor.substring(1))}&color=${color.substring(1)}&format=${format}`;
      setPreviewURL(imgUrl);
    }, 1000),
    [getValues],
  );

  const createQR = async () => {
    try {
      setLoading(true);

      const payload = {
        ...getValues(),
        userId: loggedInUser._id,
        data: QRUtils.transformDataBasedOnType(dto.data, dto.dataType),
        size: Number(dto.size),
      };

      await dispatch(QRActions.createQR(payload)).unwrap();
      await dispatch(
        QRActions.getUserQRs({
          userId: loggedInUser._id,
          meta: PageMeta.create(),
        }),
      ).unwrap();
      setPreviewURL('');

      setOpen(false);

      reset({
        color: '#000000',
        bgcolor: '#ffffff',
        format: getValues('format'),
        dataType: DataType.URL,
        size: 250,
        data: '',
      });

      AtiraToast.success(t('qr.create.success'));
    } catch (error) {
      console.log(error);
      AtiraToast.apiError(error);
      onSetTypedCRUDErrors(error, setError);
    } finally {
      setLoading(false);
    }
  };

  const getDataInputType = () => {
    switch (selectedDataType) {
      case DataType.EMAIL:
        return 'email';
      case DataType.PHONE:
      case DataType.SMS:
        return 'tel';
      case DataType.URL:
        return 'url';
      case DataType.TEXT:
      default:
        return 'text';
    }
  };

  const getDataInputPlaceholder = () => {
    switch (selectedDataType) {
      case DataType.EMAIL:
        return t('qr.create.datatype.email.placeholder');
      case DataType.PHONE:
        return t('qr.create.datatype.phone.placeholder');
      case DataType.SMS:
        return t('qr.create.datatype.sms.placeholder');
      case DataType.URL:
        return t('qr.create.datatype.url.placeholder');
      case DataType.TEXT:
      default:
        return t('qr.create.datatype.text.placeholder');
    }
  };

  const onScrollToResponseSection = () => {
    const responseSection = document.getElementById('response-wrapper');

    if (responseSection) {
      responseSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    const QRCountLimit = Number(loggedInUser.plan?.perks?.qrCount);
    const shouldUpgrade = () => {
      if (QRCountLimit <= userQRsLength) {
        setReachedLimit(true);
        return;
      }

      setReachedLimit(false);
    };

    shouldUpgrade();
  }, [userQRsLength, loggedInUser.plan?.perks?.qrCount]);

  return (
    <Modal
      width="60rem"
      open={open}
      onClose={() => {
        if (!data) {
          setPreviewURL('');
        }
        setOpen(false);
      }}
      title={t('whatsnew.features.qrCode.title')}
      maskClosable={false}
      centered
    >
      <Wrapper>
        <CreationContent>
          <Flex flexDirection="column" gap="m">
            <StyledNotice
              info={
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  gap="s"
                >
                  <span>{t('qr.create.mobilePreview.info')}</span>
                  <Button
                    icon={faCircleArrowDown}
                    padding="0"
                    backgroundColor="transparent"
                    color="lightMain"
                    iconWidth="lg"
                    onClick={onScrollToResponseSection}
                  />
                </Flex>
              }
            />
            <Controller
              name="dataType"
              control={control}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <AtiraSelect
                  value={{
                    label:
                      t(`common.${selectedDataType}`) ||
                      t('common.dropdown.select'),
                    value: selectedDataType,
                  }}
                  options={dataTypesOptions}
                  required
                  onChange={onChange}
                  title={t('qr.create.type')}
                  valid={!error}
                  errorMessage={error?.message}
                  info={t('qr.create.type.info')}
                />
              )}
            />

            <Controller
              name="data"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('common.this_field_required'),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <StyledInput
                  type={getDataInputType()}
                  placeholder={getDataInputPlaceholder()}
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    generateQRPreview();
                    if (!e.target.value) {
                      setPreviewURL('');
                    }
                  }}
                  title={t('qr.create.paragraph')}
                  required
                  valid={!error}
                  errorMessage={error?.message}
                  info={t('qr.create.paragraph.info')}
                />
              )}
            />

            <Controller
              name="size"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <StyledInput
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    generateQRPreview();
                  }}
                  type="number"
                  title={t('qr.create.input.size.label')}
                  valid={!error}
                  errorMessage={error?.message}
                  info={t('qr.create.input.size.label.info')}
                />
              )}
            />

            <Controller
              name="format"
              control={control}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <AtiraSelect
                  value={{
                    label: selectedFormat || t('common.dropdown.select'),
                    value: selectedFormat,
                  }}
                  options={formatOptions}
                  onChange={(value: any) => {
                    onChange(value);
                    generateQRPreview();
                  }}
                  containerStyle={{ width: '100%' }}
                  title={t('qr.create.input.image_type.label')}
                  valid={!error}
                  errorMessage={error?.message}
                  info={t('qr.create.input.image_type.label.info')}
                />
              )}
            />

            <Flex gap="l" flexWrap="wrap">
              <Controller
                name="color"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Flex flexDirection="column" gap="s">
                    <Flex>
                      <Tooltip title={t('qr.create.input.color.label.info')}>
                        <AtiraIcon icon={faInfoCircle} color="fgPrimary" />
                        {/* don't delete this span */}
                        <span style={{ marginInlineEnd: '0.2rem' }}></span>
                      </Tooltip>
                      <Text>{t('qr.create.input.color.label')}</Text>
                      <OptionalSpan>({t('common.optional')})</OptionalSpan>
                    </Flex>
                    <Tooltip title={t('common.pick')}>
                      <MiniColorPicker
                        value={value}
                        onChange={(c) => {
                          onChange(c.toHexString());
                          generateQRPreview();
                        }}
                        style={{ padding: '0' }}
                      />
                    </Tooltip>
                  </Flex>
                )}
              />

              <Controller
                name="bgcolor"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Flex flexDirection="column" gap="s">
                    <Flex>
                      <Tooltip
                        title={t('qr.create.input.background_color.label.info')}
                      >
                        <AtiraIcon icon={faInfoCircle} color="fgPrimary" />
                        {/* don't delete this span */}
                        <span style={{ marginInlineEnd: '0.2rem' }}></span>
                      </Tooltip>
                      <Text>{t('qr.create.input.background_color.label')}</Text>
                      <OptionalSpan>({t('common.optional')})</OptionalSpan>
                    </Flex>
                    <Tooltip title={t('common.pick')}>
                      <MiniColorPicker
                        value={value}
                        onChange={(c) => {
                          onChange(c.toHexString());
                          generateQRPreview();
                        }}
                        style={{ padding: '0' }}
                      />
                    </Tooltip>
                  </Flex>
                )}
              />
            </Flex>
          </Flex>
          <CreateButton
            loading={loading}
            onClick={
              !reachedLimit
                ? handleSubmit(createQR)
                : () => setPlansModalVisible(true)
            }
            disabled={!data}
            icon={reachedLimit ? faCrown : undefined}
          >
            {t(!reachedLimit ? 'common.save' : 'common.upgrade')}
          </CreateButton>
        </CreationContent>

        <ResponseWrapper id="response-wrapper">
          {previewURL ? (
            <Flex
              flexDirection="column"
              gap="m"
              alignItems="center"
              justifyContent="center"
            >
              <Text align="center" fontWeight={'bold'}>
                {t('qr.create.preview.title')}
              </Text>
              <Flex flexDirection="column" alignItems="center">
                <AtiraImage
                  src={previewURL}
                  width={getValues('size').toString()}
                  height={getValues('size').toString()}
                  style={{ maxWidth: '8rem', maxHeight: '8rem' }}
                />

                <Text color="fgMuted" fontSize="m" direction="ltr">
                  {`${dto.size} x ${dto.size}`}
                </Text>
              </Flex>
              <Text align="center" fontSize="s">
                {t(
                  `qr.create.preview.description${isOnMobile ? '.mobile' : ''}`,
                )}
              </Text>
            </Flex>
          ) : (
            <AtiraEmpty
              iconHeight="5rem"
              fontSize="m"
              icon={faInfoCircle}
              description={t(`qr.create.initial${isOnMobile ? '.mobile' : ''}`)}
            />
          )}
        </ResponseWrapper>
      </Wrapper>
    </Modal>
  );
};
